import React, { useMemo, useState } from "react";
import { StaticImage } from "gatsby-plugin-image";
import styled from "styled-components";

const StyledAboutPage = styled.main`
  width: 100%;
`;

const HeroSection = styled.section`
  max-width: 1260px;
  margin: 0 auto;
  padding: 170px clamp(28px, 6vw, 80px) 110px;

  .grid {
    display: grid;
    grid-template-columns: minmax(0, 1.15fr) minmax(280px, 0.85fr);
    gap: 56px;
    align-items: start;

    @media (max-width: 980px) {
      grid-template-columns: 1fr;
      gap: 36px;
    }
  }

  .eyebrow {
    margin: 0 0 18px;
    color: var(--text-muted);
    font-family: var(--font-mono);
    font-size: 13px;
    letter-spacing: 0.16em;
    text-transform: uppercase;
  }

  h1 {
    margin: 0 0 22px;
    color: var(--text-primary);
    font-size: clamp(42px, 6vw, 82px);
    line-height: 0.98;
    letter-spacing: -0.05em;
    max-width: 760px;
  }

  .lead {
    margin: 0 0 24px;
    max-width: 760px;
    color: var(--text-secondary);
    font-size: clamp(19px, 2vw, 24px);
    line-height: 1.8;
  }

  .lead strong {
    color: var(--text-primary);
    font-weight: 600;
  }

  .summary {
    max-width: 760px;
    color: var(--text-secondary);
    font-size: 17px;
    line-height: 1.95;
  }

  .summary p {
    margin: 0 0 18px;
  }

  .summary p:last-child {
    margin-bottom: 0;
  }

  .photoPanel {
    padding: 20px;
    border-radius: 28px;
    background: rgba(255, 255, 255, 0.42);
    border: 1px solid rgba(255, 255, 255, 0.55);
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    box-shadow:
      0 12px 34px rgba(0, 0, 0, 0.045),
      inset 0 1px 0 rgba(255, 255, 255, 0.36);
  }

  .photoWrap {
    overflow: hidden;
    border-radius: 22px;
  }

  .meta {
    margin-top: 18px;
    display: grid;
    gap: 12px;
  }

  .metaItem {
    padding: 14px 16px;
    border-radius: 18px;
    background: rgba(255, 255, 255, 0.45);
    border: 1px solid var(--border-light);
  }

  .metaLabel {
    margin: 0 0 6px;
    color: var(--text-muted);
    font-family: var(--font-mono);
    font-size: 12px;
    letter-spacing: 0.12em;
    text-transform: uppercase;
  }

  .metaValue {
    color: var(--text-primary);
    font-size: 15px;
    line-height: 1.7;
  }
`;

const PositioningSection = styled.section`
  max-width: 1260px;
  margin: 0 auto;
  padding: 0 clamp(28px, 6vw, 80px) 110px;

  .grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 18px;

    @media (max-width: 980px) {
      grid-template-columns: 1fr;
    }
  }

  .card {
    padding: 28px;
    border-radius: 24px;
    background: rgba(255, 255, 255, 0.42);
    border: 1px solid rgba(255, 255, 255, 0.55);
    backdrop-filter: blur(18px);
    -webkit-backdrop-filter: blur(18px);
    box-shadow:
      0 10px 30px rgba(0, 0, 0, 0.04),
      inset 0 1px 0 rgba(255, 255, 255, 0.35);
  }

  .cardTitle {
    margin: 0 0 10px;
    color: var(--text-primary);
    font-size: 24px;
    line-height: 1.2;
    letter-spacing: -0.03em;
  }

  .cardText {
    color: var(--text-secondary);
    font-size: 15px;
    line-height: 1.85;
  }
`;

const SectionIntro = styled.div`
  max-width: 1260px;
  margin: 0 auto;
  padding: 0 clamp(28px, 6vw, 80px) 26px;

  .eyebrow {
    margin: 0 0 12px;
    color: var(--text-muted);
    font-family: var(--font-mono);
    font-size: 13px;
    letter-spacing: 0.16em;
    text-transform: uppercase;
  }

  h2 {
    margin: 0;
    color: var(--text-primary);
    font-size: clamp(34px, 4.4vw, 58px);
    line-height: 1.06;
    letter-spacing: -0.045em;
    max-width: 760px;
  }

  p {
    margin: 18px 0 0;
    max-width: 760px;
    color: var(--text-secondary);
    font-size: 17px;
    line-height: 1.9;
  }
`;

const StackShell = styled.section`
  max-width: 1260px;
  margin: 0 auto;
  padding: 0 clamp(28px, 6vw, 80px) 150px;
`;

const StackHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  flex-wrap: wrap;

  .left {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .title {
    margin: 0;
    color: var(--text-primary);
    font-size: 18px;
    font-weight: 600;
  }

  .sub {
    margin: 0;
    max-width: 720px;
    color: var(--text-secondary);
    font-size: 15px;
    line-height: 1.8;
  }
`;

const SearchWrap = styled.div`
  input {
    width: min(320px, 75vw);
    padding: 12px 14px;
    border-radius: 14px;
    border: 1px solid var(--border-light);
    background: rgba(255, 255, 255, 0.42);
    color: var(--text-primary);
    font-family: var(--font-mono);
    font-size: 13px;
    outline: none;
    backdrop-filter: blur(14px);
    -webkit-backdrop-filter: blur(14px);
    transition: var(--transition);
  }

  input::placeholder {
    color: var(--text-muted);
  }

  input:focus {
    border-color: var(--border-medium);
    background: rgba(255, 255, 255, 0.62);
  }
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 16px;
`;

const Chip = styled.button`
  padding: 9px 14px;
  border-radius: 999px;
  border: 1px solid ${({ $active }) => ($active ? "rgba(17,17,17,0.18)" : "var(--border-light)")};
  background: ${({ $active }) => ($active ? "rgba(255,255,255,0.72)" : "rgba(255,255,255,0.38)")};
  color: ${({ $active }) => ($active ? "var(--text-primary)" : "var(--text-secondary)")};
  font-family: var(--font-mono);
  font-size: 12px;
  line-height: 1;
  cursor: pointer;
  transition: var(--transition);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);

  &:hover {
    transform: translateY(-1px);
    border-color: var(--border-medium);
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
  margin-top: 22px;

  @media (max-width: 980px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  @media (max-width: 720px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const TechLinkCard = styled.a`
  display: block;
  padding: 16px;
  border-radius: 18px;
  text-decoration: none;
  background: rgba(255, 255, 255, 0.42);
  border: 1px solid rgba(255, 255, 255, 0.55);
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
  box-shadow:
    0 10px 30px rgba(0, 0, 0, 0.04),
    inset 0 1px 0 rgba(255, 255, 255, 0.35);
  transition: var(--transition);

  &:hover {
    transform: translateY(-2px);
    box-shadow:
      0 14px 34px rgba(0, 0, 0, 0.06),
      inset 0 1px 0 rgba(255, 255, 255, 0.42);
  }

  .top {
    display: flex;
    align-items: center;
    gap: 12px;
    min-height: 42px;
  }

  .logo {
    width: 40px;
    height: 40px;
    border-radius: 12px;
    display: grid;
    place-items: center;
    background: rgba(255, 255, 255, 0.55);
    border: 1px solid var(--border-light);
    flex-shrink: 0;
  }

  .logo svg {
    width: 22px;
    height: 22px;
    display: block;
  }

  .name {
    margin: 0;
    color: var(--text-primary);
    font-size: 15px;
    font-weight: 600;
    line-height: 1.25;
  }

  .meta {
    margin-top: 12px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
  }

  .tag {
    font-family: var(--font-mono);
    font-size: 11px;
    color: var(--text-secondary);
    background: rgba(255, 255, 255, 0.62);
    border: 1px solid var(--border-light);
    padding: 6px 10px;
    border-radius: 999px;
    white-space: nowrap;
  }

  .hint {
    font-family: var(--font-mono);
    font-size: 11px;
    color: var(--text-muted);
    white-space: nowrap;
  }
`;

const EmptyState = styled.div`
  margin-top: 18px;
  padding: 18px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.42);
  border: 1px solid var(--border-light);
  color: var(--text-secondary);
  font-size: 14px;
`;

const tools = [
  { name: 'Python', category: 'Data', href: 'https://www.python.org/', logo: 'python' },
  { name: 'SQL', category: 'Data', href: 'https://www.postgresql.org/', logo: 'database' },
  { name: 'PostgreSQL', category: 'Data', href: 'https://www.postgresql.org/', logo: 'postgres' },
  { name: 'MySQL', category: 'Data', href: 'https://www.mysql.com/', logo: 'mysql' },
  { name: 'SQLite', category: 'Data', href: 'https://www.sqlite.org/', logo: 'sqlite' },
  { name: 'MongoDB', category: 'Data', href: 'https://www.mongodb.com/', logo: 'mongodb' },
  { name: 'Pandas', category: 'Data', href: 'https://pandas.pydata.org/', logo: 'pandas' },
  { name: 'NumPy', category: 'Data', href: 'https://numpy.org/', logo: 'numpy' },
  { name: 'Jupyter', category: 'Data', href: 'https://jupyter.org/', logo: 'jupyter' },
  { name: 'Databricks', category: 'Data', href: 'https://www.databricks.com/', logo: 'databricks' },
  { name: 'PySpark', category: 'Data', href: 'https://spark.apache.org/docs/latest/api/python/', logo: 'spark' },

  { name: 'Scikit-learn', category: 'AI/ML', href: 'https://scikit-learn.org/', logo: 'sklearn' },
  { name: 'TensorFlow', category: 'AI/ML', href: 'https://www.tensorflow.org/', logo: 'tensorflow' },
  { name: 'Keras', category: 'AI/ML', href: 'https://keras.io/', logo: 'keras' },
  { name: 'PyTorch', category: 'AI/ML', href: 'https://pytorch.org/', logo: 'pytorch' },
  { name: 'Hugging Face', category: 'AI/ML', href: 'https://huggingface.co/', logo: 'huggingface' },

  { name: 'Power BI', category: 'BI', href: 'https://powerbi.microsoft.com/', logo: 'powerbi' },
  { name: 'Tableau', category: 'BI', href: 'https://www.tableau.com/', logo: 'tableau' },
  { name: 'Excel', category: 'BI', href: 'https://www.microsoft.com/en-au/microsoft-365/excel', logo: 'excel' },
  { name: 'Matplotlib', category: 'BI', href: 'https://matplotlib.org/', logo: 'matplotlib' },
  { name: 'Seaborn', category: 'BI', href: 'https://seaborn.pydata.org/', logo: 'seaborn' },
  { name: 'Plotly', category: 'BI', href: 'https://plotly.com/', logo: 'plotly' },
  { name: 'Streamlit', category: 'BI', href: 'https://streamlit.io/', logo: 'streamlit' },

  { name: 'Apache Airflow', category: 'Engineering', href: 'https://airflow.apache.org/', logo: 'airflow' },
  { name: 'dbt', category: 'Engineering', href: 'https://www.getdbt.com/', logo: 'dbt' },
  { name: 'Docker', category: 'Engineering', href: 'https://www.docker.com/', logo: 'docker' },
  { name: 'Kubernetes', category: 'Engineering', href: 'https://kubernetes.io/', logo: 'kubernetes' },
  { name: 'Git', category: 'Engineering', href: 'https://git-scm.com/', logo: 'git' },
  { name: 'GitHub', category: 'Engineering', href: 'https://github.com/', logo: 'github' },
  { name: 'Linux', category: 'Engineering', href: 'https://www.linux.org/', logo: 'linux' },
  { name: 'Bash', category: 'Engineering', href: 'https://www.gnu.org/software/bash/', logo: 'bash' },
  { name: 'Postman', category: 'Engineering', href: 'https://www.postman.com/', logo: 'postman' },

  { name: 'React', category: 'Web', href: 'https://react.dev/', logo: 'react' },
  { name: 'JavaScript', category: 'Web', href: 'https://developer.mozilla.org/en-US/docs/Web/JavaScript', logo: 'javascript' },
  { name: 'HTML', category: 'Web', href: 'https://developer.mozilla.org/en-US/docs/Web/HTML', logo: 'html' },
  { name: 'CSS', category: 'Web', href: 'https://developer.mozilla.org/en-US/docs/Web/CSS', logo: 'css' },
  { name: 'Flask', category: 'Web', href: 'https://flask.palletsprojects.com/', logo: 'flask' },

  { name: 'Google Cloud', category: 'Cloud', href: 'https://cloud.google.com/', logo: 'gcp' },
  { name: 'Microsoft Azure', category: 'Cloud', href: 'https://azure.microsoft.com/', logo: 'azure' },

  { name: 'C++', category: 'Tools', href: 'https://isocpp.org/', logo: 'cplusplus' },
  { name: 'MATLAB', category: 'Tools', href: 'https://www.mathworks.com/products/matlab.html', logo: 'matlab' },
  { name: 'macOS', category: 'Tools', href: 'https://www.apple.com/macos/', logo: 'macos' },
  { name: 'Fedora', category: 'Tools', href: 'https://fedoraproject.org/', logo: 'fedora' },
].sort((a, b) => a.name.localeCompare(b.name));

const iconCommon = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
};

const ICONS = {
  database: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <ellipse cx="12" cy="6" rx="7" ry="3" {...iconCommon} />
      <path d="M5 6v6c0 1.7 3.1 3 7 3s7-1.3 7-3V6" {...iconCommon} />
      <path d="M5 12v6c0 1.7 3.1 3 7 3s7-1.3 7-3v-6" {...iconCommon} />
    </svg>
  ),
  python: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M8 8c0-2 1.6-3 4-3h3c1.7 0 3 1.3 3 3v3H9c-1.1 0-2-.9-2-2V8z" {...iconCommon} />
      <path d="M16 16c0 2-1.6 3-4 3H9c-1.7 0-3-1.3-3-3v-3h9c1.1 0 2 .9 2 2v4z" {...iconCommon} />
      <circle cx="14.7" cy="7.2" r="0.7" fill="currentColor" />
      <circle cx="9.3" cy="16.8" r="0.7" fill="currentColor" />
    </svg>
  ),
  react: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="1.6" fill="currentColor" />
      <ellipse cx="12" cy="12" rx="9" ry="3.5" {...iconCommon} />
      <ellipse cx="12" cy="12" rx="9" ry="3.5" transform="rotate(60 12 12)" {...iconCommon} />
      <ellipse cx="12" cy="12" rx="9" ry="3.5" transform="rotate(120 12 12)" {...iconCommon} />
    </svg>
  ),
  docker: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4 14h14c-.6 3-3 5-6.8 5H9.5C6.5 19 4.7 17 4 14z" {...iconCommon} />
      <path d="M7 10h2v2H7zM10 10h2v2h-2zM13 10h2v2h-2zM10 7h2v2h-2z" fill="currentColor" />
    </svg>
  ),
  git: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M14 7l-4 4" {...iconCommon} />
      <path d="M10 11l4 4" {...iconCommon} />
      <path d="M7.5 6.5a2 2 0 1 0 0 4 2 2 0 0 0 0-4z" fill="currentColor" />
      <path d="M16.5 5.5a2 2 0 1 0 0 4 2 2 0 0 0 0-4z" fill="currentColor" />
      <path d="M16.5 14.5a2 2 0 1 0 0 4 2 2 0 0 0 0-4z" fill="currentColor" />
    </svg>
  ),
  github: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 3a9 9 0 0 0-2.8 17.6c.4.1.5-.2.5-.4v-1.4c-2.2.5-2.7-.9-2.7-.9-.3-.8-.8-1-.8-1-.7-.5.1-.5.1-.5.8.1 1.2.8 1.2.8.7 1.2 1.9.9 2.4.7.1-.5.3-.9.5-1.1-1.8-.2-3.7-.9-3.7-4.1 0-.9.3-1.6.8-2.2-.1-.2-.4-1 .1-2.1 0 0 .7-.2 2.3.8a7.7 7.7 0 0 1 4.2 0c1.6-1 2.3-.8 2.3-.8.5 1.1.2 1.9.1 2.1.5.6.8 1.3.8 2.2 0 3.2-1.9 3.9-3.7 4.1.3.2.6.8.6 1.5v2.2c0 .2.1.5.5.4A9 9 0 0 0 12 3z" fill="currentColor" />
    </svg>
  ),
  gcp: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M8 17a4 4 0 0 1 0-8 5 5 0 0 1 9.6 1.4A3.5 3.5 0 1 1 17.5 17H8z" {...iconCommon} />
    </svg>
  ),
  azure: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M7 18l4-12h5l-4 12H7z" fill="currentColor" />
      <path d="M10 18l7-6 1 6h-8z" fill="currentColor" opacity="0.75" />
    </svg>
  ),
  airflow: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 3l2.4 6.2L21 12l-6.6 2.8L12 21l-2.4-6.2L3 12l6.6-2.8L12 3z" {...iconCommon} />
    </svg>
  ),
  dbt: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M7 7l10 5-10 5V7z" fill="currentColor" />
      <path d="M7 7l10 5-10 5" {...iconCommon} />
    </svg>
  ),
  spark: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 3c-2 4 2 6 0 10-1 2-4 2-4 6 0 1 .4 2 1 2h7c1 0 2-1 2-2 0-4-3-4-4-6-2-4 2-6 0-10z" {...iconCommon} />
    </svg>
  ),
  flask: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M9 3h6" {...iconCommon} />
      <path d="M10 3v6l-4 7a3 3 0 0 0 2.6 4.5h6.8A3 3 0 0 0 18 16l-4-7V3" {...iconCommon} />
    </svg>
  ),
  html: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M6 3h12l-1.2 15L12 21l-4.8-3L6 3z" {...iconCommon} />
      <path d="M9 7h6M9 11h6M9 15h6" {...iconCommon} />
    </svg>
  ),
  css: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M6 3h12l-1.2 15L12 21l-4.8-3L6 3z" {...iconCommon} />
      <path d="M9 7h6M9 11h4M9 15h6" {...iconCommon} />
    </svg>
  ),
  javascript: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M5 4h14v16H5z" {...iconCommon} />
      <path d="M10 16c0 1-.6 2-2 2-1.2 0-2-.6-2-2" {...iconCommon} />
      <path d="M14 18c2 0 3-1 3-2.4 0-3.2-5-2-5-5 0-1.2 1-2 2.4-2 1.2 0 2 .5 2.4 1.4" {...iconCommon} />
    </svg>
  ),
  sklearn: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M7 16l5-9 5 9H7z" {...iconCommon} />
      <path d="M9 14h6" {...iconCommon} />
    </svg>
  ),
  tensorflow: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M6 7l6-3 6 3" {...iconCommon} />
      <path d="M12 4v16" {...iconCommon} />
      <path d="M8 9h8" {...iconCommon} />
    </svg>
  ),
  keras: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M7 5v14M17 5l-7 7 7 7" {...iconCommon} />
    </svg>
  ),
  pytorch: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M14 6c-4 1-7 5-6 9 1.2 4 5.5 6 9.4 4.6" {...iconCommon} />
      <circle cx="15.8" cy="7.2" r="0.7" fill="currentColor" />
    </svg>
  ),
  huggingface: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="9" cy="11" r="1" fill="currentColor" />
      <circle cx="15" cy="11" r="1" fill="currentColor" />
      <path d="M7 14c1.2 1.5 2.8 2 5 2s3.8-.5 5-2" {...iconCommon} />
      <path d="M6 10c0-3 2.3-5 6-5s6 2 6 5" {...iconCommon} />
    </svg>
  ),
  pandas: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M8 5v14M12 7v10M16 5v14" {...iconCommon} />
    </svg>
  ),
  numpy: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M8 7l8 4v6l-8-4V7z" {...iconCommon} />
      <path d="M8 7l8 4" {...iconCommon} />
    </svg>
  ),
  mongodb: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 3c3 3 4 6 3 10-1 4-3 7-3 8 0-1-2-4-3-8-1-4 0-7 3-10z" {...iconCommon} />
    </svg>
  ),
  postgres: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M8 8c0-2 1.8-3 4-3s4 1 4 3-1.8 4-4 4-4-2-4-4z" {...iconCommon} />
      <path d="M10 12v6a2 2 0 0 0 4 0v-6" {...iconCommon} />
    </svg>
  ),
  mysql: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M6 14c2-6 10-8 12-2" {...iconCommon} />
      <path d="M9 14c1.5 2 4 3 7 3" {...iconCommon} />
    </svg>
  ),
  sqlite: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M7 20V9l5-6 5 6v11H7z" {...iconCommon} />
      <path d="M10 12h4M10 15h4" {...iconCommon} />
    </svg>
  ),
  databricks: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M6 8l6 3 6-3M6 12l6 3 6-3M6 16l6 3 6-3" {...iconCommon} />
    </svg>
  ),
  jupyter: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="4" {...iconCommon} />
      <circle cx="6" cy="8" r="1" fill="currentColor" />
      <circle cx="18" cy="16" r="1" fill="currentColor" />
    </svg>
  ),
  tableau: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 4v16M4 12h16M7 7h10M7 17h10" {...iconCommon} />
    </svg>
  ),
  powerbi: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M6 18V10M11 18V6M16 18v-8" {...iconCommon} />
    </svg>
  ),
  matplotlib: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M6 17l4-8 4 6 4-10" {...iconCommon} />
      <path d="M4 19h16" {...iconCommon} />
    </svg>
  ),
  seaborn: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M6 16c2-7 10-7 12 0" {...iconCommon} />
      <path d="M6 16h12" {...iconCommon} />
    </svg>
  ),
  plotly: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M7 16V8M12 16V6M17 16v-5" {...iconCommon} />
    </svg>
  ),
  streamlit: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M6 16c3-6 9-8 12-8-3 2-6 6-7 10" {...iconCommon} />
    </svg>
  ),
  postman: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="8" {...iconCommon} />
      <path d="M10 14l6-4" {...iconCommon} />
    </svg>
  ),
  excel: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M8 5h10v14H8z" {...iconCommon} />
      <path d="M6 7h4v10H6z" {...iconCommon} />
      <path d="M7 10l2 4M9 10l-2 4" {...iconCommon} />
    </svg>
  ),
  linux: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="8" r="3" {...iconCommon} />
      <path d="M8 20c0-3 1.5-5 4-5s4 2 4 5" {...iconCommon} />
      <circle cx="10.8" cy="8" r="0.4" fill="currentColor" />
      <circle cx="13.2" cy="8" r="0.4" fill="currentColor" />
    </svg>
  ),
  bash: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4 6h16v12H4z" {...iconCommon} />
      <path d="M8 10l3 2-3 2M13 14h3" {...iconCommon} />
    </svg>
  ),
  kubernetes: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="3" {...iconCommon} />
      <path d="M12 3v4M21 12h-4M12 21v-4M3 12h4M18.4 5.6l-2.8 2.8M18.4 18.4l-2.8-2.8M5.6 18.4l2.8-2.8M5.6 5.6l2.8 2.8" {...iconCommon} />
    </svg>
  ),
  cplusplus: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M9 8a4 4 0 1 0 0 8" {...iconCommon} />
      <path d="M15 10v4M13 12h4M18 10v4M16 12h4" {...iconCommon} />
    </svg>
  ),
  matlab: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M6 17l4-10 4 6 4-4" {...iconCommon} />
      <path d="M10 7l2 10" {...iconCommon} />
    </svg>
  ),
  macos: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M9 7c1-2 3-3 5-3 1 0 2 .4 3 1-1 1-2 2-2 3 0 1 .6 2 2 3-.6 2-2 4-4 4-1 0-2-.5-3-.5s-2 .5-3 .5c-2 0-4-3-4-6 0-3 2-5 4-5 1 0 2 .5 2 .5s.7-.3 0-.5z" {...iconCommon} />
    </svg>
  ),
  fedora: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 4a7 7 0 1 0 7 7V8.5A4.5 4.5 0 0 0 14.5 4H12z" {...iconCommon} />
      <path d="M10 14c0 1.7 1.3 3 3 3" {...iconCommon} />
      <circle cx="15.2" cy="9.2" r="0.7" fill="currentColor" />
    </svg>
  ),
};

function Logo({ name }) {
  return (
    <span style={{ color: 'var(--sydneyblue)', display: 'grid', placeItems: 'center' }}>
      {ICONS[name] || ICONS.database}
    </span>
  );
}

const TechStackGrid = () => {
  const [query, setQuery] = useState('');
  const [active, setActive] = useState('All');

  const categories = useMemo(
    () => ['All', ...Array.from(new Set(tools.map(tool => tool.category)))],
    [],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();

    return tools.filter(tool => {
      const matchesCategory = active === 'All' || tool.category === active;
      const matchesQuery =
        !q ||
        tool.name.toLowerCase().includes(q) ||
        tool.category.toLowerCase().includes(q);

      return matchesCategory && matchesQuery;
    });
  }, [query, active]);

  return (
    <StackShell>
      <StackHeader>
        <div className="left">
          <p className="title">Tech Stack</p>
          <p className="sub">
            Tools and platforms I use across software, analytics engineering, dashboards,
            machine learning, automation, and production-oriented delivery.
          </p>

          <Chips role="tablist" aria-label="Tech stack categories">
            {categories.map(category => {
              const selected = active === category;
              const tabId = `tab-${category}`;
              const panelId = `panel-${category}`;

              return (
                <Chip
                  key={category}
                  id={tabId}
                  type="button"
                  role="tab"
                  $active={selected}
                  aria-selected={selected}
                  aria-controls={panelId}
                  tabIndex={selected ? 0 : -1}
                  onClick={() => setActive(category)}>
                  {category}
                </Chip>
              );
            })}
          </Chips>
        </div>

        <SearchWrap>
          <input
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search tools..."
            aria-label="Search tech stack"
            spellCheck={false}
            autoComplete="off"
          />
        </SearchWrap>
      </StackHeader>

      <div id={`panel-${active}`} role="tabpanel" aria-labelledby={`tab-${active}`}>
        {filtered.length > 0 ? (
          <Grid>
            {filtered.map(tool => (
              <TechLinkCard
                key={tool.name}
                href={tool.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`${tool.name} official website`}>
                <div className="top">
                  <div className="logo" aria-hidden="true">
                    <Logo name={tool.logo} />
                  </div>
                  <p className="name">{tool.name}</p>
                </div>

                <div className="meta">
                  <span className="tag">{tool.category}</span>
                  <span className="hint">Open site</span>
                </div>
              </TechLinkCard>
            ))}
          </Grid>
        ) : (
          <EmptyState>
            No tools matched your search. Try a different keyword or category.
          </EmptyState>
        )}
      </div>
    </StackShell>
  );
};

const AboutPageContent = () => {
  return (
    <StyledAboutPage>
      <HeroSection>
        <div className="grid">
          <div>
            <p className="eyebrow">About</p>

            <h1>Building useful systems from complexity.</h1>

            <p className="lead">
              I work across <strong>software, analytics, data engineering, machine learning, and automation</strong>
              to turn messy real-world information into systems that are reliable, explainable, and genuinely useful.
            </p>

            <div className="summary">
              <p>
                My background sits at the intersection of <strong>analytics engineering, applied data science, and software development</strong>.
                I have built payroll and compliance systems, OCR extraction workflows, NLP experiments, forecasting models,
                geospatial analyses, dashboards, and decision-support tools across domains where the data is imperfect and the
                output has to stand up to real use.
              </p>

              <p>
                I am most comfortable where the problem is complex, the data is messy, and the solution needs to be both
                <strong> technically sound and practically usable</strong>. That usually means translating rules into logic,
                designing reproducible pipelines, validating outputs carefully, and building systems that do more than just
                look impressive in a notebook.
              </p>

              <p>
                Beyond work, I care about learning fast, thinking clearly, and building with intent. I enjoy travelling,
                exploring new ideas, and working on projects that connect technical depth with real-world usefulness.
              </p>
            </div>
          </div>

          <div>
            <div className="photoPanel">
              <div className="photoWrap">
                <StaticImage
                  src="../../images/me.png"
                  width={700}
                  quality={95}
                  formats={["AUTO", "WEBP", "AVIF"]}
                  alt="Headshot of Hemang Sharma"
                />
              </div>

              <div className="meta">
                <div className="metaItem">
                  <p className="metaLabel">Focus</p>
                  <div className="metaValue">
                    Software, pipelines, analytics, machine learning, and AI systems.
                  </div>
                </div>

                <div className="metaItem">
                  <p className="metaLabel">Style</p>
                  <div className="metaValue">
                    Calm design, measurable outcomes, and systems that can hold up in real environments.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </HeroSection>

      <PositioningSection>
        <div className="grid">
          <div className="card">
            <h2 className="cardTitle">Software & Automation</h2>
            <p className="cardText">
              I build tools, workflow logic, and production-oriented systems that reduce manual work and improve reliability.
            </p>
          </div>

          <div className="card">
            <h2 className="cardTitle">Data & Pipelines</h2>
            <p className="cardText">
              I work across messy datasets, analytical workflows, validation layers, and reporting environments that need to scale.
            </p>
          </div>

          <div className="card">
            <h2 className="cardTitle">Machine Learning & AI</h2>
            <p className="cardText">
              From forecasting to OCR, NLP, and deep learning, I build systems that connect models to useful outcomes.
            </p>
          </div>
        </div>
      </PositioningSection>

      <SectionIntro>
        <p className="eyebrow">Tools & Platforms</p>
        <h2>Depth across the stack.</h2>
        <p>
          A working toolkit spanning data, analytics, engineering, cloud, BI, web, and AI — chosen for practicality,
          speed, and the ability to ship useful systems.
        </p>
      </SectionIntro>

      <TechStackGrid />
    </StyledAboutPage>
  );
};

export default AboutPageContent;